import React, { useCallback, useMemo, useState } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { OrganizationPrivateNetworkingEntitlements } from 'libs/end-to-end/src/organization-private-networking-entitlements';
import { OrganizationPrivateNetworkingEntitlement } from 'libs/end-to-end/src/organization-private-networking-entitlements';
import { CreateOrganizationPrivateNetworkingEntitlementRequest } from 'libs/end-to-end/src/organization-private-networking-entitlements';
import { UpdateOrganizationPrivateNetworkingEntitlementRequest } from 'libs/end-to-end/src/organization-private-networking-entitlements';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';

/**
 * OrganizationPrivateNetworkingEntitlementsPage component
 *
 * This component renders the Private Networking Entitlements management page, allowing users to view, create, edit, and delete private networking entitlements.
 * It includes a table of entitlements and modal dialogs for creating/editing entitlements.
 */
export const OrganizationPrivateNetworkingEntitlementsPage = () => {
  const { userAuthenticatedApiCall } = useUser();

  // State for managing the create/edit entitlement modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntitlement, setEditingEntitlement] =
    useState<OrganizationPrivateNetworkingEntitlement | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Form state
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [whitelistedHosts, setWhitelistedHosts] = useState<string[]>([]);
  const [newHost, setNewHost] = useState('');

  // Add new state for error modal
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Local state for entitlements (for useCrudOperations)
  const [localEntitlements, setLocalEntitlements] = useState<
    OrganizationPrivateNetworkingEntitlement[]
  >([]);

  /**
   * Callback to get the URL for fetching organizations
   */
  const getOrganizationsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations`;
  }, []);

  /**
   * Hook for fetching and managing organizations data
   */
  const organizationsReadPattern = useSimpleReadPattern({
    getUrlFn: getOrganizationsUrlFn,
    canRead: true,
    responseSchema: Organizations,
  });

  /**
   * Callback to get the URL for fetching private networking entitlements
   */
  const getEntitlementsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organization-private-networking-entitlements`;
  }, []);

  /**
   * Hook for fetching and managing entitlements data
   */
  const entitlementsReadPattern = useSimpleReadPattern({
    getUrlFn: getEntitlementsUrlFn,
    canRead: true,
    responseSchema: OrganizationPrivateNetworkingEntitlements,
  });

  /**
   * Update local entitlements when API data changes
   */
  React.useEffect(() => {
    if (
      entitlementsReadPattern.status === 'done' &&
      'entity' in entitlementsReadPattern
    ) {
      setLocalEntitlements(entitlementsReadPattern.entity);
    }
  }, [entitlementsReadPattern.status, entitlementsReadPattern]);

  /**
   * CRUD operations for local entitlements
   */
  const crudOps = useCrudOperations({
    items: localEntitlements,
    setItems: setLocalEntitlements,
  });

  /**
   * Function to refresh the entitlements data
   */
  const refreshEntitlements = useCallback(() => {
    if (
      entitlementsReadPattern.status === 'done' ||
      entitlementsReadPattern.status === 'error'
    ) {
      entitlementsReadPattern.reloadEntity();
    }
  }, [entitlementsReadPattern]);

  /**
   * Function to reset form state
   */
  const resetForm = useCallback(() => {
    setSelectedOrganizationId('');
    setEnabled(false);
    setWhitelistedHosts([]);
    setNewHost('');
    setEditingEntitlement(null);
  }, []);

  /**
   * Function to open create modal
   */
  const openCreateModal = useCallback(() => {
    resetForm();
    setIsCreateModalOpen(true);
  }, [resetForm]);

  /**
   * Function to open edit modal
   */
  const openEditModal = useCallback(
    (entitlement: OrganizationPrivateNetworkingEntitlement) => {
      setEditingEntitlement(entitlement);
      setSelectedOrganizationId(entitlement.organizationId);
      setEnabled(entitlement.enabled);
      setWhitelistedHosts([...entitlement.whitelistedHosts]);
      setNewHost('');
      setIsEditModalOpen(true);
    },
    [],
  );

  /**
   * Function to close modals
   */
  const closeModals = useCallback(() => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    resetForm();
  }, [resetForm]);

  /**
   * Function to add a new host to the whitelist
   */
  const addHost = useCallback(() => {
    if (newHost.trim() && !whitelistedHosts.includes(newHost.trim())) {
      setWhitelistedHosts([...whitelistedHosts, newHost.trim()]);
      setNewHost('');
    }
  }, [newHost, whitelistedHosts]);

  /**
   * Function to remove a host from the whitelist
   */
  const removeHost = useCallback(
    (hostToRemove: string) => {
      setWhitelistedHosts(
        whitelistedHosts.filter((host) => host !== hostToRemove),
      );
    },
    [whitelistedHosts],
  );

  /**
   * Async callback for creating an entitlement
   */
  const createEntitlement = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive()) return;

      setIsCreating(true);
      setErrorMessage(null);

      try {
        const request: CreateOrganizationPrivateNetworkingEntitlementRequest = {
          organizationId: selectedOrganizationId,
          enabled,
          whitelistedHosts,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationPrivateNetworkingEntitlement,
          url: `${getApiBaseUrl()}/organization-private-networking-entitlements`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to create entitlement');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to create entitlement',
          );
        }

        // Add to local state immediately for better UX
        crudOps.onAdd(result.value);
        refreshEntitlements();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to create entitlement',
        );
      } finally {
        setIsCreating(false);
      }
    },
    [
      selectedOrganizationId,
      enabled,
      whitelistedHosts,
      userAuthenticatedApiCall,
      refreshEntitlements,
      closeModals,
      crudOps,
    ],
  );

  /**
   * Async callback for updating an entitlement
   */
  const updateEntitlement = useAsyncCallback(
    async (isAlive) => {
      if (!isAlive() || !editingEntitlement) return;

      setIsUpdating(true);
      setErrorMessage(null);

      try {
        const request: UpdateOrganizationPrivateNetworkingEntitlementRequest = {
          enabled,
          whitelistedHosts,
        };

        const result = await userAuthenticatedApiCall({
          responseSchema: OrganizationPrivateNetworkingEntitlement,
          url: `${getApiBaseUrl()}/organization-private-networking-entitlements/${editingEntitlement.id}`,
          requestOptions: {
            method: 'PUT',
            body: JSON.stringify(request),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        });

        if (!result) {
          throw new Error('Failed to update entitlement');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to update entitlement',
          );
        }

        // Update local state immediately for better UX
        const index = localEntitlements.findIndex(
          (e) => e.id === editingEntitlement.id,
        );
        if (index !== -1) {
          crudOps.onEdit(index, result.value);
        }
        refreshEntitlements();
        closeModals();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to update entitlement',
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [
      editingEntitlement,
      enabled,
      whitelistedHosts,
      userAuthenticatedApiCall,
      refreshEntitlements,
      closeModals,
      localEntitlements,
      crudOps,
    ],
  );

  /**
   * Async callback for deleting an entitlement
   */
  const deleteEntitlement = useAsyncCallback(
    async (isAlive, entitlement: OrganizationPrivateNetworkingEntitlement) => {
      if (!isAlive()) return;

      try {
        const result = await userAuthenticatedApiCall({
          responseSchema: EmptyObject,
          url: `${getApiBaseUrl()}/organization-private-networking-entitlements/${entitlement.id}`,
          requestOptions: {
            method: 'DELETE',
          },
        });

        if (!result) {
          throw new Error('Failed to delete entitlement');
        }

        if (result.resultType === 'err') {
          throw new Error(
            (result.error.errorReason as string) ||
              'Failed to delete entitlement',
          );
        }

        refreshEntitlements();
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Failed to delete entitlement',
        );
      }
    },
    [userAuthenticatedApiCall, refreshEntitlements, localEntitlements, crudOps],
  );

  /**
   * Get organization name by ID
   */
  const getOrganizationName = useCallback(
    (organizationId: string) => {
      if (
        organizationsReadPattern.status !== 'done' ||
        !('entity' in organizationsReadPattern)
      ) {
        return organizationId;
      }

      const organization = organizationsReadPattern.entity.organizations.find(
        (org: { id: string; name: string }) => org.id === organizationId,
      );
      return organization?.name || organizationId;
    },
    [organizationsReadPattern],
  );

  /**
   * Table column definitions
   */
  const columnDefinitions = useMemo(
    () => [
      {
        id: 'organization',
        header: () => <div>Organization</div>,
        cell: ({ item }: { item: OrganizationPrivateNetworkingEntitlement }) =>
          getOrganizationName(item.organizationId),
      },
      {
        id: 'status',
        header: () => <div>Status</div>,
        cell: ({
          item,
        }: {
          item: OrganizationPrivateNetworkingEntitlement;
        }) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.enabled
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {item.enabled ? 'Enabled' : 'Disabled'}
          </span>
        ),
      },
      {
        id: 'hostCount',
        header: () => <div>Host Count</div>,
        cell: ({ item }: { item: OrganizationPrivateNetworkingEntitlement }) =>
          item.whitelistedHosts.length.toString(),
      },
      {
        id: 'whitelistedHosts',
        header: () => <div>Whitelisted Hosts</div>,
        cell: ({
          item,
        }: {
          item: OrganizationPrivateNetworkingEntitlement;
        }) => (
          <div
            className="max-w-xs truncate"
            title={item.whitelistedHosts.join(', ')}
          >
            {item.whitelistedHosts.join(', ') || 'None'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => <div>Actions</div>,
        cell: ({
          item,
        }: {
          item: OrganizationPrivateNetworkingEntitlement;
        }) => (
          <div className="flex gap-2">
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => openEditModal(item),
              }}
              text="Edit"
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => deleteEntitlement(item),
              }}
              text="Delete"
            />
          </div>
        ),
      },
    ],
    [getOrganizationName, openEditModal, deleteEntitlement],
  );

  return (
    <AdminLayout currentPage={'OrganizationPrivateNetworkingEntitlements'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Private Networking Entitlements',
            to: '/organization-private-networking-entitlements',
          },
        ]}
      />
      <p>You can manage private networking entitlements from this screen.</p>

      <S2Typography
        variant="headline-4"
        translatedContent="Private Networking Entitlements"
      />
      <div className="flex flex-col items-start">
        <Button
          behavior={{
            type: 'onClick',
            onClick: openCreateModal,
          }}
          text="New Private Networking Entitlement"
        />
      </div>

      <Table
        items={localEntitlements}
        columnDefinitions={columnDefinitions}
        isLoading={entitlementsReadPattern.status === 'loading'}
        empty={
          <div className="text-center py-8 text-gray-500">
            No private networking entitlements found
          </div>
        }
      />

      {/* Create/Edit Modal */}
      <Transition
        appear
        show={isCreateModalOpen || isEditModalOpen}
        as={Fragment}
      >
        <Dialog as="div" className="relative z-overlay" onClose={closeModals}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isCreateModalOpen
                      ? 'Create Private Networking Entitlement'
                      : 'Edit Private Networking Entitlement'}
                  </Dialog.Title>

                  <div className="mt-4 space-y-4">
                    {isCreateModalOpen && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Organization
                        </label>
                        <select
                          value={selectedOrganizationId}
                          onChange={(e) =>
                            setSelectedOrganizationId(e.target.value)
                          }
                          className="form-select mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          <option value="">Select an organization</option>
                          {organizationsReadPattern.status === 'done' &&
                            'entity' in organizationsReadPattern &&
                            organizationsReadPattern.entity.organizations.map(
                              (org: { id: string; name: string }) => (
                                <option key={org.id} value={org.id}>
                                  {org.name}
                                </option>
                              ),
                            )}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                          className="form-checkbox rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Enabled
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Whitelisted Hosts
                      </label>
                      <div className="mt-1 flex gap-2">
                        <TextInput
                          type="text"
                          data-qa="newHostInput"
                          value={newHost}
                          onChange={(e) =>
                            setNewHost((e.target as HTMLInputElement).value)
                          }
                          placeholder="Enter host (e.g., example.com)"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addHost();
                            }
                          }}
                        />
                        <Button
                          behavior={{
                            type: 'onClick',
                            onClick: addHost,
                          }}
                          text="Add"
                        />
                      </div>
                      {whitelistedHosts.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {whitelistedHosts.map((host, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between rounded bg-gray-100 px-2 py-1"
                            >
                              <span className="text-sm">{host}</span>
                              <Button
                                behavior={{
                                  type: 'onClick',
                                  onClick: () => removeHost(host),
                                }}
                                text="Remove"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {errorMessage && (
                    <div className="mt-4 rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700">{errorMessage}</div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3">
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={closeModals}
                      disabled={isCreating || isUpdating}
                    >
                      Cancel
                    </LegacyButton>
                    <LegacyButton
                      variant={LegacyButtonVariant.PRIMARY_LIGHT}
                      onClick={
                        isCreateModalOpen
                          ? createEntitlement
                          : updateEntitlement
                      }
                      disabled={
                        isCreating ||
                        isUpdating ||
                        (isCreateModalOpen && !selectedOrganizationId)
                      }
                    >
                      {isCreating || isUpdating
                        ? 'Saving...'
                        : isCreateModalOpen
                          ? 'Create'
                          : 'Update'}
                    </LegacyButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </AdminLayout>
  );
};
